import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import {
  bindPreparation,
  preparationBody,
  confirm,
  history,
  remember,
  receipt,
  recover,
  submit,
  type Data,
  type Reference,
} from './workflow'

export function useWorkflow() {
  const { user } = useAuth()
  const account = user?.id ?? ''
  const identity = `${account}:${user?.authScope}:${user?.role}:${user?.tenantId ?? ''}`
  const current = useRef(identity)
  current.current = identity
  const epoch = useRef(0)
  const invalidate = useCallback(() => {
    epoch.current++
  }, [])
  const inFlight = useRef(false)
  const [lastReference, setLastReference] = useState<Reference | null>(null)
  const [visibleIdentity, setVisibleIdentity] = useState(identity)
  const [references, setReferences] = useState<Reference[]>([])
  const [view, setView] = useState<Data | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [broken, setBroken] = useState(false)
  useEffect(() => {
    invalidate()
    inFlight.current = false
    setLastReference(null)
    setVisibleIdentity(identity)
    setView(null)
    setError('')
    setBusy(false)
    setBroken(false)
    try {
      setReferences(account ? history(account) : [])
    } catch {
      setReferences([])
      setBroken(true)
      setError(
        'Original command history is unavailable. Restore this browser history before submitting another command.'
      )
    }
    return invalidate
  }, [identity, account, invalidate])
  async function perform(ref: Reference, body?: Data, replay = false) {
    if (!account || current.current !== identity || inFlight.current || broken) return
    inFlight.current = true
    const owner = identity,
      version = ++epoch.current
    const live = () => current.current === owner && epoch.current === version
    setBusy(true)
    setError('')
    setView(null)
    let submitted = false
    try {
      if (body) {
        if (history(account).some(r => r.status === 'pending' && (!replay || r.kind !== 'prepare' || r.target !== ref.target || r.command !== ref.command)))
          throw new Error('Recover the pending command before submitting another command.')
        if (ref.kind === 'prepare') ref = bindPreparation(ref, body)
        remember(account, ref)
        if (live()) setReferences(history(account))
      }
      if (body) submitted = true
      const data = body ? await submit(ref, body) : await recover(ref)
      if (!live()) return
      const checked = receipt(data, ref, ref.kind === 'accept' ? account : undefined)
      confirm(account, ref)
      if (live()) {
        setReferences(history(account))
        setView(checked)
        setLastReference(ref)
        if (ref.kind === 'cancel' && checked.state === 'issued')
          setError(
            'The original decision was issued. No-effect cancellation was not recorded;use governed appointment revocation.'
          )
        if (ref.kind === 'issue' && checked.state === 'no_effect')
          setError('The original decision is no-effect. This intent cannot issue an appointment.')
      }
      return checked
    } catch (e) {
      if (live())
        setError(
          e instanceof Error && e.message.includes('Recover the pending')
            ? e.message
            : body && !submitted
              ? 'The original command reference could not be saved. Nothing was submitted;restore browser storage before trying again.'
              : 'Outcome unconfirmed. Keep the original reference and use Check original outcome. Do not create a replacement command. Check your current session and MFA if access was refused.'
        )
    } finally {
      if (live()) {
        inFlight.current = false
        setBusy(false)
      }
    }
  }
  async function run(ref: Reference, body?: Data) { return perform(ref, body) }
  async function retryPreparation(ref: Reference) {
    try {
      const original = history(account).find(r => r.kind === 'prepare' && r.command === ref.command && r.target === ref.target && r.status === 'pending')
      if (!original) throw new Error()
      return await perform(original, preparationBody(original), true)
    } catch {
      setError('Saved original preparation references are unavailable. Keep the original intent and restore its references;do not create a replacement.')
    }
  }
  const visible = visibleIdentity === identity
  return {
    lastReference: visible ? lastReference : null,
    references: visible ? references : [],
    view: visible ? view : null,
    busy: visible ? busy : false,
    error: visible ? error : '',
    broken,
    run,
    retryPreparation,
    locked: !visible || broken || busy || references.some(r => r.status === 'pending'),
  }
}
